import React, { useCallback, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { PillBadge } from '@lightspeed/flame/Badge';

import {
  ERROR_CACHE_DURATION_MS,
  getNotificationData,
  isCacheValid,
  NOTIFICATION_STYLES,
  setNotificationData,
  TILE_KEYS,
} from 'root/apps/components/Notifications/utils';
import { ENVIRONMENTS, getEnvFromHostname } from 'root/apps/pages/utils/getEnvFromHostname';
import Modules from 'root/apps/shared_modules/Modules';
import { datadogLogs } from 'root/datadog/datadogLogs';

import { MAS_CONFIG } from './shared/config';

const StyledPillBadge = styled(PillBadge)`
  margin-right: 10px;
`;

/**
 * Sidebar-specific MAS notification badge component.
 *
 * This component is self-contained and handles:
 * 1. Checking cached notification data (valid for 2 hours)
 * 2. Fetching MAS notifications from the API if cache is stale
 * 3. Updating localStorage with the notification count and timestamp
 * 4. Dispatching events to notify other components (e.g., TileNotificationCount)
 * 5. Rendering the notification badge in the sidebar
 *
 * This avoids wrapping the entire Sidebar with a context provider.
 *
 * Feature flags:
 *   - subscription.merchant_account_space (MAS UI must be active)
 *   - subscription.invoice_notification (notification feature must be enabled)
 * Required permission: admin_shops (user must have access to Billing & Subscription page)
 */
export const SidebarMASNotificationBadge: React.FC = () => {
  // Check feature flags synchronously on component initialization
  const isMerchantAccountSpaceActive = Modules.hasModule('subscription.merchant_account_space');
  const isNotificationFeatureEnabled = Modules.hasModule('subscription.invoice_notification');
  // Check if user has permission to access Billing & Subscription page
  const hasAdminShopsRight = window.merchantos?.session?.hasRight('admin_shops');

  const isEnabled =
    isMerchantAccountSpaceActive && isNotificationFeatureEnabled && hasAdminShopsRight;

  const [notificationCount, setNotificationCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const checkNotifications = useCallback(async () => {
    // Skip fetching if feature is not enabled or user lacks permission
    if (!isEnabled) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Check if we have valid cached data (within 2 hours)
      if (isCacheValid(TILE_KEYS.BILLING_SUBSCRIPTION)) {
        const cachedData = getNotificationData(TILE_KEYS.BILLING_SUBSCRIPTION);
        setNotificationCount(cachedData.count);
        setIsLoading(false);
        return;
      }

      // Wait for session to be initialized
      const getSubscriptionId = (): Promise<string> => {
        return new Promise((resolve) => {
          const id = window.merchantos?.session?.getSystemCustomerID();
          if (id && id !== 0) {
            resolve(id.toString());
            return;
          }

          // Session not ready, wait for init
          if (window.merchantos?.session?.init) {
            window.merchantos.session.init().done(() => {
              resolve((window.merchantos?.session?.getSystemCustomerID() || 0).toString());
            });
          } else {
            resolve('0');
          }
        });
      };

      const subscriptionId = await getSubscriptionId();

      if (!subscriptionId || subscriptionId === '0') {
        setIsLoading(false);
        return;
      }

      const baseUrl = MAS_CONFIG.getBaseUrl();
      const url = `${baseUrl}/v0/products/lsr/subscriptions/${subscriptionId}/notifications`;

      const headers: Record<string, string> = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };

      // In non-production environments, add test mode headers
      const env = getEnvFromHostname();
      if (env !== ENVIRONMENTS.PRODUCTION) {
        headers['X-Mas-Test-Mode'] = 'true';
        headers['X-Mas-Test-Notification-Button-Type'] = 'payment-failure-single';
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch notifications: ${response.status}`);
      }

      const data = await response.json();

      // Log API response for production debugging (essential diagnostic log)
      const notificationsCount = Array.isArray(data?.data) ? data.data.length : 0;
      datadogLogs.logger.info('LSR-36587 MASNotificationBadge: API response', {
        subscriptionId,
        notificationsCount,
        notificationCodes: Array.isArray(data?.data)
          ? data.data.map((n: { notificationCode?: string }) => n.notificationCode)
          : [],
        env,
      });

      // Check if there are any notifications in the response
      const count = data?.data && Array.isArray(data.data) ? data.data.length : 0;
      const finalCount = count > 0 ? 1 : 0;

      setNotificationCount(finalCount);

      // Update localStorage and dispatch event to notify TileNotificationCount components
      setNotificationData(TILE_KEYS.BILLING_SUBSCRIPTION, finalCount, NOTIFICATION_STYLES.DANGER);
    } catch (err) {
      datadogLogs.logger.error('LSR-36587 MASNotificationBadge: Fetch failed', {
        error: err instanceof Error ? err.message : String(err),
        tileKey: TILE_KEYS.BILLING_SUBSCRIPTION,
      });
      setNotificationCount(0);
      // Use shorter TTL (30 minutes) on error to allow faster retry
      setNotificationData(
        TILE_KEYS.BILLING_SUBSCRIPTION,
        0,
        NOTIFICATION_STYLES.DANGER,
        ERROR_CACHE_DURATION_MS
      );
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled]);

  // Check notifications on mount
  useEffect(() => {
    checkNotifications();
  }, [checkNotifications]);

  if (!isEnabled) {
    return null;
  }

  const hasNotifications = notificationCount > 0;

  if (isLoading || !hasNotifications) {
    return null;
  }

  return <StyledPillBadge type="danger">{notificationCount}</StyledPillBadge>;
};
